import { enqueue, flushOutbox, newKey } from './outbox.js';

const TOKEN_KEY = 'diesel-token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  constructor(message, { status = 0, isNetworkError = false, fromCache = false } = {}) {
    super(message);
    this.status = status;
    this.isNetworkError = isNetworkError;
    this.fromCache = fromCache;
  }
}

const onUnauthorized = new Set();
export function subscribeUnauthorized(fn) {
  onUnauthorized.add(fn);
  return () => onUnauthorized.delete(fn);
}

function buildUrl(path, params) {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
  }
  return url.pathname + url.search;
}

async function request(method, path, { body, params, idempotencyKey } = {}) {
  const headers = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('No connection to the server', { isNetworkError: true });
  }

  if (response.status === 401) {
    onUnauthorized.forEach((fn) => fn());
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(payload.error || 'Session expired', { status: 401 });
  }

  const fromCache = response.headers.get('X-From-Cache') === 'true';

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(payload?.error || `Request failed (${response.status})`, {
      status: response.status,
      fromCache,
    });
  }

  if (fromCache && payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return Object.defineProperty(payload, '__fromCache', { value: true, enumerable: false });
  }
  return payload;
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  patch: (path, body) => request('PATCH', path, { body }),
  put: (path, body) => request('PUT', path, { body }),
  del: (path) => request('DELETE', path),

  /**
   * POST that survives a dead connection.
   *
   * If the network is down and `queueOffline` is set, the payload goes into the
   * outbox and the caller gets `{ queued: true }` back. The submission replays,
   * once, when the connection returns.
   */
  async post(path, body, { queueOffline = false, label } = {}) {
    const idempotencyKey = newKey();
    try {
      return await request('POST', path, { body, idempotencyKey });
    } catch (err) {
      if (err.isNetworkError && queueOffline) {
        await enqueue({ path, body, idempotencyKey, label: label || path });
        return { queued: true };
      }
      throw err;
    }
  },
};

/**
 * Downloads a file from an authenticated endpoint and hands it to the browser.
 * A plain link cannot carry the bearer token, so the body is fetched and saved
 * through a blob URL instead.
 */
export async function downloadFile(path, fallbackName) {
  const token = getToken();
  let response;
  try {
    response = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  } catch {
    throw new ApiError('No connection to the server', { isNetworkError: true });
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(payload?.error || `Download failed (${response.status})`, { status: response.status });
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = response.headers.get('X-Backup-Filename') || match?.[1] || fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename, bytes: blob.size };
}

/** Replays anything the outbox is holding. Called on reconnect and on demand. */
export function syncOutbox() {
  return flushOutbox((item) =>
    request('POST', item.path, { body: item.body, idempotencyKey: item.idempotencyKey }),
  );
}

/** Cached API responses belong to whoever was signed in - drop them on logout. */
export function clearApiCache() {
  navigator.serviceWorker?.controller?.postMessage('CLEAR_API_CACHE');
}
